import React from 'react';
import { Box, Typography } from '@mui/material';
import { elliottTokens as t } from '../tokens';
import ElliottFace from '../../../../components/ElliottFace';
import { useIsMobileApp } from '../../../../../../mobile/mobileApp';

// Elliott's face — the same character as the voice stage, the framework orb and
// the Add Program chat, so he reads as one person everywhere he speaks.
export const ElliottAvatar: React.FC<{ size?: number }> = ({ size = 40 }) => (
  <ElliottFace size={size} glow />
);

// Shared body-copy style for Elliott messages + takeaway, so all thread text renders identically.
// (Rendered via MUI Typography to avoid the app's global `p { font-size … !important }` rules.)
export const messageBodySx = {
  color: t.textPrimary,
  fontSize: `${t.bodySize}px`,
  lineHeight: `${t.bodyLineHeight}px`,
  fontWeight: 400,
  // Preserve the sender's formatting (line breaks, spacing, lists) instead of collapsing it.
  whiteSpace: 'pre-wrap',
  overflowWrap: 'break-word'
} as const;

// Optional status tag shown next to the ELLIOTT label (e.g. "Live Scenario" with a live dot).
export type ElliottTag = { label: string; dot?: boolean };

type ElliottMessageProps = {
  tag?: ElliottTag;
  children: React.ReactNode;
  /** Phone only: the avatar beside the ELLIOTT label and the message the full width under them (a lesson, an Assessment). */
  stacked?: boolean;
};

const ElliottMessage: React.FC<ElliottMessageProps> = ({ tag, children, stacked = false }) => {
  const isMobileApp = useIsMobileApp();
  const label = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      <Typography
        sx={{
          fontSize: 14,
          fontWeight: 700,
          letterSpacing: '1.8px',
          lineHeight: 'normal',
          backgroundImage: t.brandyTextGradient,
          WebkitBackgroundClip: 'text',
          backgroundClip: 'text',
          color: 'transparent'
        }}
      >
        ELLIOTT
      </Typography>
      {tag && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          {tag.dot && (
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                bgcolor: t.success,
                boxShadow: `0 0 6px ${t.success}`,
                animation: 'elliottLiveDot 1.6s ease-in-out infinite',
                '@keyframes elliottLiveDot': { '0%, 100%': { opacity: 1 }, '50%': { opacity: 0.4 } }
              }}
            />
          )}
          <Typography sx={{ fontSize: 14, color: t.textTertiary, lineHeight: 'normal' }}>{tag.label}</Typography>
        </Box>
      )}
    </Box>
  );
  const body = (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px', '& strong': { fontWeight: 700 } }}>{children}</Box>
  );
  // Phone, in a lesson: the avatar beside the ELLIOTT label, and the message the full
  // width under them: a lesson is mostly reading, and a column beside the avatar was narrow.
  // Only the first child and the styles switch, so the body keeps its place in the tree and
  // a rotation across the breakpoint never remounts it (a video's position, a streamed text).
  const phone = isMobileApp && stacked;
  return (
    <Box
      sx={
        phone
          ? { display: 'flex', flexDirection: 'column', gap: '8px', width: '100%', py: '4px' }
          : { display: 'flex', gap: '20px', alignItems: 'flex-start', width: '100%', pr: '68px' }
      }
    >
      {phone ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <ElliottAvatar size={28} />
          {label}
        </Box>
      ) : (
        <ElliottAvatar />
      )}
      <Box
        sx={
          phone
            ? { display: 'flex', flexDirection: 'column' }
            : {
                flex: 1,
                minWidth: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: '6px',
                py: '12px' // no horizontal padding: text aligns to the column edge (avatar + gap = 60px)
              }
        }
      >
        {!phone && label}
        {body}
      </Box>
    </Box>
  );
};

export default ElliottMessage;
